import React, { useState, useRef, useEffect } from 'react';
import Card from '../../UI/Card/Card';
import { toast } from 'react-toastify';
import './ImageAnnotation.css';

const ImageAnnotation = ({
  imageData,
  onSave,
  onNext,
  onPrevious,
  onCancel,
  currentIndex,
  totalImages,
  onToggleOverflow,
  onTogglePType,
  onToggleDiscard,
  onToggleBlur
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [rectangles, setRectangles] = useState([]);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('question'); // 'question' or 'solution'
  const [selectedRectIndex, setSelectedRectIndex] = useState(null);
  const [isDraggingRect, setIsDraggingRect] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });


  // Load saved rectangles when image changes
  console.log(imageData)
  useEffect(() => {
    if (imageData && imageData.annotations) {
      // Annotations are already normalized (0-1), store them as-is
      setRectangles(imageData.annotations || []);
    } else {
      setRectangles([]);
    }
  }, [imageData?.imgPath]);

  // Redraw canvas when window resizes or zoom changes
  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current && canvasRef.current && imageLoaded) {
        const img = imageRef.current;
        const canvas = canvasRef.current;
        
        const displayWidth = img.offsetWidth || img.clientWidth;
        const displayHeight = img.offsetHeight || img.clientHeight;
        
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        
        // Update state to trigger re-render
        setCanvasDimensions({ width: displayWidth, height: displayHeight });
      }
    };

    window.addEventListener('resize', handleResize);
    // Also listen for zoom events
    window.visualViewport?.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [imageLoaded]);


  // Draw rectangles on canvas
  useEffect(() => {
    if (!canvasRef.current || !imageLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw all rectangles (convert from normalized to pixel coords)
    rectangles.forEach((rect, index) => {
      const pixelRect = {
        x: rect.x * canvas.width,
        y: rect.y * canvas.height,
        width: rect.width * canvas.width,
        height: rect.height * canvas.height
      };
      
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.strokeRect(pixelRect.x, pixelRect.y, pixelRect.width, pixelRect.height);
    });

    // Draw current rectangle being drawn
    if (currentRect) {
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
      ctx.setLineDash([]);
    }
  }, [rectangles, currentRect, imageLoaded, canvasDimensions]);

  const handleImageLoad = () => {
    if (imageRef.current && canvasRef.current) {
      const img = imageRef.current;
      const canvas = canvasRef.current;

      // Wait for next frame to ensure layout is complete
      requestAnimationFrame(() => {
        const displayWidth = img.offsetWidth || img.clientWidth;
        const displayHeight = img.offsetHeight || img.clientHeight;

        // Set canvas internal resolution to match display size
        canvas.width = displayWidth;
        canvas.height = displayHeight;

        setImageLoaded(true);
        setCanvasDimensions({ width: displayWidth, height: displayHeight });
      });
    }
  };

  const handleOverflowClick = () => {
    onToggleOverflow(imageData);
  };

  const handlePTypeClick = () => {
    onTogglePType(imageData);
  };

  const handleDiscardClick = () => {
    onToggleDiscard(imageData);
  };

  const handleBlurClick = () => {
    onToggleBlur(imageData);
  };



  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return { x: 0, y: 0 };

    // Get canvas bounding rect
    const canvasRect = canvas.getBoundingClientRect();
    // Get image bounding rect to calculate offset
    const imgRect = img.getBoundingClientRect();

    // Calculate mouse position relative to canvas
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;

    return { x, y };
  };

  const handleMouseDown = (e) => {
    if (!imageLoaded) return;
  
    const pos = getMousePos(e);
    const canvas = canvasRef.current;
  
    // Convert mouse position to normalized coords for checking
    const normalizedPos = {
      x: pos.x / canvas.width,
      y: pos.y / canvas.height
    };
  
    const rectIndex = getRectAtPosition(normalizedPos.x, normalizedPos.y);
  
    if (rectIndex !== null) {
      // Start dragging existing rectangle
      const rect = rectangles[rectIndex];
      setSelectedRectIndex(rectIndex);
      setIsDraggingRect(true);
      setDragOffset({
        x: normalizedPos.x - rect.x,
        y: normalizedPos.y - rect.y
      });
      return;
    }
  
    // Start drawing new rectangle (in pixel coords for smooth drawing)
    setIsDrawing(true);
    setStartPos(pos);
    setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
  };
  

  const handleMouseMove = (e) => {
    if (!imageLoaded) return;
  
    const pos = getMousePos(e);
    const canvas = canvasRef.current;
  
    // Drag logic - work in normalized coords
    if (isDraggingRect && selectedRectIndex !== null) {
      const normalizedPos = {
        x: pos.x / canvas.width,
        y: pos.y / canvas.height
      };
      
      const updatedRects = [...rectangles];
      updatedRects[selectedRectIndex] = {
        ...updatedRects[selectedRectIndex],
        x: normalizedPos.x - dragOffset.x,
        y: normalizedPos.y - dragOffset.y
      };
  
      setRectangles(updatedRects);
      return;
    }
  
    // Drawing logic - work in pixel coords for smooth preview
    if (!isDrawing) return;
  
    const width = pos.x - startPos.x;
    const height = pos.y - startPos.y;
  
    setCurrentRect({
      x: width < 0 ? pos.x : startPos.x,
      y: height < 0 ? pos.y : startPos.y,
      width: Math.abs(width),
      height: Math.abs(height)
    });
  };
  

  const handleMouseUp = (e) => {
    // Stop dragging
    if (isDraggingRect) {
      setIsDraggingRect(false);
      setSelectedRectIndex(null);
      return;
    }
  
    // Finish drawing
    if (!isDrawing || !imageLoaded) return;
  
    const pos = getMousePos(e);
    const canvas = canvasRef.current;
    const width = pos.x - startPos.x;
    const height = pos.y - startPos.y;
  
    if (Math.abs(width) > 10 && Math.abs(height) > 10) {
      // Convert pixel coords to normalized coords before storing
      const newRect = {
        x: (width < 0 ? pos.x : startPos.x) / canvas.width,
        y: (height < 0 ? pos.y : startPos.y) / canvas.height,
        width: Math.abs(width) / canvas.width,
        height: Math.abs(height) / canvas.height
      };
      setRectangles([...rectangles, newRect]);
    }
  
    setIsDrawing(false);
    setCurrentRect(null);
  };
  

  const handleUndo = () => {
    if (rectangles.length > 0) {
      setRectangles(rectangles.slice(0, -1));
    }
  };

  const getRectAtPosition = (normalizedX, normalizedY) => {
    // Check rectangles in reverse order (top-most first)
    for (let i = rectangles.length - 1; i >= 0; i--) {
      const rect = rectangles[i];
      if (
        normalizedX >= rect.x &&
        normalizedX <= rect.x + rect.width &&
        normalizedY >= rect.y &&
        normalizedY <= rect.y + rect.height
      ) {
        return i;
      }
    }
    return null;
  };
  

  const handleClearAll = () => {
    setRectangles([]);
  };

  const handleSave = () => {
    if (!canvasRef.current) return;

    // Rectangles are already stored in normalized coords (0-1 range)
    // Just format them to 4 decimal places
    const normalizedRects = rectangles.map(r => ({
      x: parseFloat(r.x.toFixed(4)),
      y: parseFloat(r.y.toFixed(4)),
      width: parseFloat(r.width.toFixed(4)),
      height: parseFloat(r.height.toFixed(4))
    }));

    const updatedImageData = {
      ...imageData,
      annotations: normalizedRects,
      isDiscard: imageData?.isDiscard || false,
      isBlur: imageData?.isBlur || false,
    };

    onSave(updatedImageData);
  };


  const handleSaveAndNext = () => {
    handleSave();
    onNext();
  };

  // Construct image URL - handle both relative and absolute paths
  const getImageUrl = () => {
    const path = imageData?.imgPath || '';
    if (!path) return '';

    // If path starts with /, it's relative to domain

    return `https://cdnqms.motion.ac.in/${path}`;


    // Otherwise, assume it's a relative path from CDN or base URL
    // Common CDN pattern: https://cdnqms.motion.ac.in/uploads/...
    if (path.includes('uploads/') || path.includes('userfiles/')) {
      return `https://cdnqms.motion.ac.in/${path.replace(/^\//, '')}`;
    }

    // Default: prepend base URL
    return `https://qmstest.motion.ac.in/apiseq/api/v1/${path}`;
  };

  const imageUrl = getImageUrl();

  return (
    <div className="image-annotation-container">
      <div className="annotation-header">
        <div className="annotation-header-left">
          <button className="back-btn" onClick={onCancel}>
            ← Back to Table
          </button>
          <div className="annotation-tabs">
            <button
              className={`tab-btn ${activeTab === 'question' ? 'active' : ''}`}
              onClick={() => setActiveTab('question')}
            >
              Question Image
            </button>
            <button
              className={`tab-btn ${activeTab === 'solution' ? 'active' : ''}`}
              onClick={() => setActiveTab('solution')}
            >
              Solution Image
            </button>
          </div>
        </div>
        <div className="annotation-info">
          Image {currentIndex + 1} of {totalImages}
        </div>
      </div>

      <Card className="annotation-card">
        <div className="annotation-content">
          <div className="image-wrapper">
            {imageUrl ? (
              <>
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Question"
                  onLoad={handleImageLoad}
                  className="annotation-image"
                />
                <canvas
                  ref={canvasRef}
                  className="annotation-canvas"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={() => {
                    setIsDrawing(false);
                    setCurrentRect(null);
                  }}
                />
              </>
            ) : (
              <div className="no-image-message">No image available</div>
            )}
          </div>

          <div className="annotation-sidebar">
            <div className="annotation-actions">
              <button
                className={`action-btn mark-overflow ${imageData?.isOverflow ? 'active' : ''}`}
                onClick={handleOverflowClick}
              >
                {imageData?.isOverflow ? 'Clear Overflow' : 'Mark Overflow'}
              </button>

              <button
                className={`action-btn mark-ptype ${imageData?.isPType ? 'active' : ''}`}
                onClick={handlePTypeClick}
              >
                {imageData?.isPType ? 'Clear P-type' : 'Mark P-type'}
              </button>

              <button
                className={`action-btn mark-discard ${imageData?.isDiscard ? 'active' : ''}`}
                onClick={handleDiscardClick}
              >
                {imageData?.isDiscard ? 'Undo Image Discard' : 'Image Discard'}
              </button>

              <button
                className={`action-btn mark-blur ${imageData?.isBlur ? 'active' : ''}`}
                onClick={handleBlurClick}
              >
                {imageData?.isBlur ? 'Clear Request Deblur' : 'Request Deblur'}
              </button>

              <button className="action-btn undo" onClick={handleUndo}>
                Undo Last
              </button>
              <button className="action-btn clear" onClick={handleClearAll}>
                Clear All
              </button>
              <button className="action-btn previous" onClick={onPrevious} disabled={currentIndex === 0}>
                Previous Image
              </button>
              <button className="action-btn save" onClick={handleSave}>
                Save
              </button>
              <button className="action-btn save-next primary" onClick={handleSaveAndNext}>
                Save & Next
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ImageAnnotation;

